/**
 * 账号：名字即账号，uid 由服务器发放，跨设备唯一且终身不变。
 *
 * 没登录 / 连不上服务器时退化为「本机身份」：uid 存在 localStorage，
 * 换台机器（或清了浏览器数据）就会变成另一个人——所以跨设备串门请先注册。
 *
 * identity 是一个「就地更新」的对象：NetProxy 直接引用它，
 * 登录成功只改字段，不换对象，重连、换房间都不会把自己变成两个人。
 */

const ACCOUNT_KEY = 'dtj_account';   // { uid, name, token, verified }
const UID_KEY = 'dtj_uid';           // 本机身份的 uid
const TAB_KEY = 'dtj_tab';           // 本标签页的会话号（sessionStorage）

/** Node / 无 localStorage 环境下退化成内存存储，冒烟测试照样能跑 */
const memory = new Map();
function readKey(scope, k) {
  try {
    const s = scope === 'session' ? sessionStorage : localStorage;
    return s.getItem(k);
  } catch { return memory.get(scope + ':' + k) || null; }
}
function writeKey(scope, k, v) {
  try {
    const s = scope === 'session' ? sessionStorage : localStorage;
    s.setItem(k, v);
  } catch { memory.set(scope + ':' + k, v); }
}
function dropKey(scope, k) {
  try {
    const s = scope === 'session' ? sessionStorage : localStorage;
    s.removeItem(k);
  } catch { memory.delete(scope + ':' + k); }
}

function rand(n = 10) {
  const abc = 'abcdefghijklmnopqrstuvwxyz0123456789';
  let s = '';
  for (let i = 0; i < n; i++) s += abc[Math.floor(Math.random() * abc.length)];
  return s;
}

/** 本机身份：没有账号时用它，稳定但不跨设备 */
export function localUid() {
  let uid = readKey('local', UID_KEY);
  if (!uid) { uid = 'anon-' + rand(10); writeKey('local', UID_KEY, uid); }
  return uid;
}

/** 一个标签页一个号：同机多开时用来区分两个窗口 */
export function tabId() {
  let t = readKey('session', TAB_KEY);
  if (!t) { t = rand(6); writeKey('session', TAB_KEY, t); }
  return t;
}

async function callApi(body, extraHeaders = {}) {
  let res;
  try {
    res = await fetch('/api/account', {
      method: 'POST',
      headers: { 'content-type': 'application/json', ...extraHeaders },
      body: JSON.stringify(body),
    });
  } catch (e) {
    throw new Error('连不上账号服务（本机预览没有 /api/account，部署后才有），先用本机身份。');
  }
  let data = null;
  try { data = await res.json(); } catch { /* 非 JSON，走下面统一报错 */ }
  if (!res.ok || (data && data.error)) {
    throw new Error((data && data.error) || ('账号服务返回 ' + res.status));
  }
  return data || {};
}

export class Account {
  constructor() {
    this.identity = { uid: localUid(), name: '', token: '', verified: false, tab: tabId() };
    const saved = readKey('local', ACCOUNT_KEY);
    if (saved) {
      try {
        const p = JSON.parse(saved);
        if (p && p.uid) {
          this.identity.uid = p.uid;
          this.identity.name = p.name || '';
          this.identity.token = p.token || '';
          this.identity.verified = !!p.verified;
        }
      } catch { /* 存档坏了就当没登录 */ }
    }
  }

  get uid() { return this.identity.uid; }
  get name() { return this.identity.name || ''; }
  get verified() { return !!this.identity.verified; }

  _apply(data) {
    this.identity.uid = data.uid;
    this.identity.name = data.name;
    this.identity.token = data.token;
    this.identity.verified = true;
    writeKey('local', ACCOUNT_KEY, JSON.stringify({
      uid: data.uid, name: data.name, token: data.token, verified: true,
    }));
    return data;
  }

  async signup(name, pass) {
    const data = await callApi({ action: 'signup', name, pass });
    return this._apply(data);
  }

  async login(name, pass) {
    const data = await callApi({ action: 'login', name, pass });
    return this._apply(data);
  }

  /** 退出：uid 退回本机身份，服务器上的账号还在 */
  logout() {
    dropKey('local', ACCOUNT_KEY);
    this.identity.uid = localUid();
    this.identity.name = '';
    this.identity.token = '';
    this.identity.verified = false;
  }

  /**
   * 注销账号：把服务器上的号删掉，名字可以让别人再用。
   * 口令可留空——留空就拿当前登录凭证当凭据（已经登录过就不用再输一遍）。
   */
  async remove(pass = '') {
    if (!this.verified) throw new Error('还没登录，没什么可注销的。');
    const name = this.identity.name;
    await callApi({ action: 'delete', name, pass, token: this.identity.token });
    this.logout();
    return name;
  }

  /** 管理员：看名单（要书库口令，等于书架上传那个口令） */
  async adminList(adminToken) {
    return await callApi({ action: 'list' }, { 'x-token': adminToken });
  }

  /** 管理员：按名字清号（要书库口令） */
  async adminRemove(name, adminToken) {
    return await callApi({ action: 'delete', name }, { 'x-token': adminToken });
  }
}
